import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaEnvelope, FaChevronRight } from 'react-icons/fa';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
};

const logoVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

const textVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      delay: 0.2,
      ease: 'easeOut',
    },
  },
};

const linkVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

const contactItemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.5,
      ease: 'easeOut',
    },
  },
};

const Footer = () => {
  const handleEmailClick = (e) => {
    try {
      window.open(
        'https://mail.google.com/mail/?view=cm&to=contact@theaiunion.com',
        '_blank',
        'noopener,noreferrer'
      );
    } catch (error) {
      console.error('Gmail link failed:', error);
      window.location.href = 'mailto:contact@theaiunion.com';
    }
  };

  return (
    <motion.footer
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ amount: 0.2 }}
      transition={{ duration: 0.5 }}
      className="relative z-10 py-10 text-white font-openSans border-t border-[#0A2472] bg-[#063970] mt-auto"
    >
      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 lg:px-25 grid grid-cols-1 md:grid-cols-3 gap-y-8 md:gap-x-12">
        {/* Logo and A.I. UNION Text */}
        <motion.div
          className="flex flex-col sm:flex-row items-center md:items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.img
            src="/logo.png"
            alt="The A.I. Union Footer Logo"
            className="w-13 h-10"
            variants={logoVariants}
          />
          <motion.h1
            className="text-xl text-white uppercase font-bold"
            variants={textVariants}
          >
            The AI Union
          </motion.h1>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          className="flex flex-col items-center md:items-start"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h4
            className="text-lg font-semibold text-gray-200 mb-4 uppercase"
            variants={textVariants}
          >
            Quick Links
          </motion.h4>
          <div className="flex flex-col space-y-2 text-sm text-gray-300">
            {[ 
              { name: 'Home', path: '/' },
              { name: 'AI Insights', path: '/ai-insights' },
              { name: 'AI Hire', path: '/ai-hire' },
              { name: 'Services', path: '/services' },
              { name: 'About Us', path: '/about' },
            ].map((link, i) => (
              <motion.div
                key={link.name}
                variants={linkVariants}
                custom={i}
                whileHover={{ x: 5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <Link
                  to={link.path}
                  className="hover:text-[#2596be] transition duration-300 flex items-center"
                >
                  <FaChevronRight className="mr-2 text-xs text-[#2596be]" />
                  {link.name}
                </Link>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Contact Information */}
        <motion.div
          className="flex flex-col items-center md:items-start"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h4
            className="text-lg font-semibold text-gray-200 mb-4 uppercase"
            variants={textVariants}
          >
            Contact Us
          </motion.h4>
          <motion.div
            className="space-y-2 text-sm text-gray-100"
            variants={contactItemVariants}
          >
            <motion.p
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <FaEnvelope className="mr-2 text-[#2596be] flex-shrink-0" />
              <a
                href="mailto:contact@theaiunion.com"
                onClick={handleEmailClick}
                className="hover:text-[#2596be] transition duration-300"
              >
                contact@theaiunion.com
              </a>
            </motion.p>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom Copyright with Privacy Policy and Terms of Use */}
      <motion.div
        className="relative z-10 mt-8 pt-4 text-center text-xs text-gray-200"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
      >
        © 2025 The AI Union All Rights Reserved.{' '}
        <Link
          to="/privacy-policy"
          className="hover:text-[#2596be] transition duration-300 mx-2"
        >
          Privacy Policy
        </Link>
        |{' '}
        <Link
          to="/terms-of-use"
          className="hover:text-[#2596be] transition duration-300 mx-2"
        >
          Terms of Use
        </Link>
      </motion.div>
    </motion.footer>
  );
};

export default Footer;
